import { Box } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Children,
  isValidElement,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';

const OPTION_ROW_HEIGHT = 34;
const OPTION_OVERSCAN = 8;
const OPTION_LIST_PADDING = 8;

type FocusableOptionProps = {
  data?: unknown;
  isFocused?: boolean;
};

export function VirtualizedOptions({
  children,
  focusedOption,
  paddingEnd = OPTION_LIST_PADDING,
  paddingStart = OPTION_LIST_PADDING,
  scrollElement,
  selectionCount,
  suppressFocusScroll,
  viewportHeight,
}: {
  children: ReactNode;
  focusedOption?: unknown;
  paddingEnd?: number;
  paddingStart?: number;
  scrollElement: HTMLElement | null;
  selectionCount?: number;
  suppressFocusScroll?: MutableRefObject<boolean>;
  viewportHeight: number;
}) {
  const items = useMemo(() => Children.toArray(children), [children]);
  const focusedIndex = items.findIndex(
    (item) =>
      isValidElement<FocusableOptionProps>(item) &&
      (item.props.isFocused || item.props.data === focusedOption),
  );
  const virtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => OPTION_ROW_HEIGHT,
    getItemKey: (index) => {
      const item = items[index];
      return isValidElement(item) && item.key !== null ? item.key : index;
    },
    getScrollElement: () => scrollElement,
    initialRect: {
      height: viewportHeight,
      width: 0,
    },
    overscan: OPTION_OVERSCAN,
    paddingEnd,
    paddingStart,
  });
  const previousFocusedIndex = useRef(focusedIndex);
  const previousSelectionCount = useRef(selectionCount);

  useEffect(() => {
    const focusChanged = previousFocusedIndex.current !== focusedIndex;
    const selectionChanged = previousSelectionCount.current !== selectionCount;
    previousFocusedIndex.current = focusedIndex;
    previousSelectionCount.current = selectionCount;

    if ((focusChanged || selectionChanged) && suppressFocusScroll?.current) {
      suppressFocusScroll.current = false;
    } else if (focusChanged && focusedIndex >= 0) {
      virtualizer.scrollToIndex(focusedIndex, { align: 'auto' });
    }
  }, [focusedIndex, selectionCount, suppressFocusScroll, virtualizer]);

  return (
    <Box height={`${virtualizer.getTotalSize()}px`} position="relative" width="full">
      {virtualizer.getVirtualItems().map((virtualItem) => (
        <Box
          key={virtualItem.key}
          ref={virtualizer.measureElement}
          data-index={virtualItem.index}
          position="absolute"
          top={0}
          transform={`translateY(${virtualItem.start}px)`}
          width="full"
        >
          {items[virtualItem.index]}
        </Box>
      ))}
    </Box>
  );
}
